/**
 * Types for third-party subscription integrations (Recharge, Skio).
 * For native Redo subscriptions, see @redotech/redo-model/subscriptions
 */
import { createEnum } from "@redotech/zod/zod-enum";
import * as z from "zod/v4";
import { FrontendProductSchema } from "../agents/support-agents/frontend-product";

export const SubscriptionIntegrationType = {
  RECHARGE: "recharge",
  SKIO: "skio",
} as const;

export type SubscriptionIntegrationTypeValue =
  (typeof SubscriptionIntegrationType)[keyof typeof SubscriptionIntegrationType];
const subscriptionIntegrationTypes = Object.values(
  SubscriptionIntegrationType,
) as SubscriptionIntegrationTypeValue[];
const SubscriptionIntegrationTypeSchema = createEnum(
  subscriptionIntegrationTypes,
);

export const SubscriptionIntegrationStatus = {
  ACTIVE: "active",
  CANCELLED: "cancelled",
  EXPIRED: "expired",
} as const;

export type SubscriptionIntegrationStatusValue =
  (typeof SubscriptionIntegrationStatus)[keyof typeof SubscriptionIntegrationStatus];
const subscriptionIntegrationStatuses = Object.values(
  SubscriptionIntegrationStatus,
) as SubscriptionIntegrationStatusValue[];
const SubscriptionIntegrationStatusSchema = createEnum(
  subscriptionIntegrationStatuses,
);

export const SubscriptionIntegrationIntervalUnit = {
  DAY: "day",
  WEEK: "week",
  MONTH: "month",
} as const;

export type SubscriptionIntegrationIntervalUnitValue =
  (typeof SubscriptionIntegrationIntervalUnit)[keyof typeof SubscriptionIntegrationIntervalUnit];
const subscriptionIntegrationIntervalUnits = Object.values(
  SubscriptionIntegrationIntervalUnit,
) as SubscriptionIntegrationIntervalUnitValue[];
const SubscriptionIntegrationIntervalUnitSchema = createEnum(
  subscriptionIntegrationIntervalUnits,
);

const SubscriptionIntegrationAddressSchema_ = z.object({
  address1: z.string().optional(),
  address2: z.string().optional(),
  city: z.string().optional(),
  province: z.string().optional(),
  zip: z.string().optional(),
  country: z.string().optional(),
});

export const SubscriptionIntegrationAddressSchema: z.ZodType<SubscriptionIntegrationAddress> =
  SubscriptionIntegrationAddressSchema_;
export interface SubscriptionIntegrationAddress
  extends z.infer<typeof SubscriptionIntegrationAddressSchema_> {}

const SubscriptionIntegrationSchema_ = z.object({
  id: z.number(),
  customerId: z.number(),
  type: SubscriptionIntegrationTypeSchema,
  status: SubscriptionIntegrationStatusSchema,
  quantity: z.number(),
  price: z.string(),
  variantExternalId: z.string().optional(),
  orderIntervalUnit: SubscriptionIntegrationIntervalUnitSchema.optional(),
  orderIntervalFrequency: z.number().optional(),
  chargeIntervalFrequency: z.number().optional(),
  nextChargeScheduledAt: z.string().optional(),
  cancelledAt: z.string().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
  address: SubscriptionIntegrationAddressSchema.optional(),
  product: FrontendProductSchema.optional(),
});

export const SubscriptionIntegrationSchema: z.ZodType<SubscriptionIntegration> =
  SubscriptionIntegrationSchema_;
export interface SubscriptionIntegration
  extends z.infer<typeof SubscriptionIntegrationSchema_> {}
